import { Box, Icon, IconButton, Popover, Text, VStack } from '@chakra-ui/react'
import { Info } from 'lucide-react'
import React, { useState } from 'react'

export interface InfoIconTooltipProps {
  /** Tooltip title */
  title: string
  /** Tooltip content - can be ReactNode for rich content */
  content: React.ReactNode
  /** Optional aria-label for the icon button */
  ariaLabel?: string
  /** Icon size - 'small' for 14px, 'default' for 24px */
  iconSize?: 'small' | 'default'
}

/**
 * Info icon with tooltip for providing contextual help
 * Uses an info icon (i) instead of question mark
 * Click to open/close - no hover behavior to prevent layout shifts
 */
export const InfoIconTooltip: React.FC<InfoIconTooltipProps> = ({
  title,
  content,
  ariaLabel = 'Information',
  iconSize = 'default',
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const isSmall = iconSize === 'small'
  const buttonSize = isSmall ? '14px' : '24px'
  const iconBoxSize = isSmall ? 2.5 : 4

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(!isOpen)
  }

  return (
    <Box display="inline-flex" flexShrink={0}>
      <Popover.Root
        open={isOpen}
        onOpenChange={(e) => setIsOpen(e.open)}
        positioning={{ placement: 'top-start' }}
        closeOnInteractOutside={true}
      >
        <Popover.Trigger asChild>
          <IconButton
            size="xs"
            variant="ghost"
            aria-label={ariaLabel}
            aria-expanded={isOpen}
            onClick={handleToggle}
            onMouseDown={(e) => e.stopPropagation()}
            colorPalette="brand"
            bg="bg.panel"
            borderRadius="full"
            minW={buttonSize}
            w={buttonSize}
            h={buttonSize}
            p={0}
            flexShrink={0}
            _hover={{
              bg: 'blue.subtle',
            }}
          >
            <Icon boxSize={iconBoxSize}>
              <Info size="100%" />
            </Icon>
          </IconButton>
        </Popover.Trigger>
        <Popover.Positioner>
          <Popover.Content
            className="nodrag nowheel nopan"
            maxW="400px"
            w="max-content"
            minW="200px"
            maxH="80vh"
            bg="bg.panel"
            borderColor="border.default"
            boxShadow="xl"
            p={4}
            zIndex={9999}
            overflow="hidden"
            style={{
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              zIndex: 9999,
            }}
          >
            <VStack align="stretch" gap={3} overflow="hidden" w="100%">
              <Text
                fontSize="md"
                fontWeight="bold"
                color="fg.default"
                wordBreak="break-word"
                overflowWrap="break-word"
                style={{ wordWrap: 'break-word' }}
              >
                {title}
              </Text>
              <Box
                fontSize="sm"
                color="fg.default"
                lineHeight="1.6"
                wordBreak="break-word"
                overflowWrap="break-word"
                overflowY="auto"
                maxH="calc(80vh - 80px)"
                w="100%"
                style={{ wordWrap: 'break-word' }}
                className="nodrag"
                onWheel={(e) => e.stopPropagation()}
              >
                {content}
              </Box>
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Popover.Root>
    </Box>
  )
}
